<template>
    <validation-observer ref="form" v-slot="{ meta, handleSubmit, errors, setFieldValue }">
        <modal
            :id="modalId"
            title="Settings"
            ok-title="Save"
            :ok-disabled="!meta.valid || loading"
            @ok.prevent="handleSubmit(_, save)"
            @hide="hideModal"
            @hidden="hiddenModal"
            @shown="setData"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="handleSubmit(_, save)" v-if="values">
                <b-row>
                    <b-col>
                        <label>Commission:</label>
                        <validation-provider name="commission" rules="required|validFloat" >
                            <form-input
                                v-model="values.commission"
                                :rightIcon="['fas', 'percent']"
                                :error="errors.commission"
                                @input="val => setFieldValue('commission', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider
                            name="rate_rules_export_period"
                            rules="required"
                        >
                            <form-select
                                label="Rate/Rules Export Period:"
                                v-model="values.rate_rules_export_period"
                                :options="rateRulesExportPeriodOptions"
                                :error="errors.rate_rules_export_period"
                                @change="val => setFieldValue('rate_rules_export_period', val)"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="mt-4">
                    <b-col>
                        <b-alert show variant="info">
                            Airbnb allows Instant Book only when using the integration. Request to Book will only be
                            utilized when it is a) enabled and b) when the instant book window has closed or guest
                            qualifications for instant book have not been met.
                        </b-alert>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="booking_lead_time" rules="required" >
                            <form-select
                                label="Booking Lead Time:"
                                v-model="values.booking_lead_time"
                                :options="bookingLeadTimeOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="mb-4">
                    <b-col>
                        <form-select
                            label="Allow Request to Book:"
                            v-model="values.allow_request_to_book"
                            :options="allowRequestToBookOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row v-if="partnerSettings.messaging_api_allowed">
                    <b-col>
                        <form-select
                            label="Fetch Inquiries:"
                            v-model="values.fetch_inquiries"
                            :options="fetchInquiriesOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            label="Damage Protection Fee:"
                            v-model="values.add_damage_to_cleaning_fee"
                            :options="addDamageToCleaningFeeOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            label="Management Fee:"
                            v-model="values.add_mgmt_fee_to_cleaning_fee"
                            :options="addMgmtFeeToCleaningFeeOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="house_rules" >
                            <form-textarea
                                label="House Rules (HTML and links are not allowed):"
                                rows="5"
                                v-model="values.house_rules"
                                :error="errors.house_rules"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <a href="https://www.airbnb.com/rooms/sync" target="_blank">Go to Airbnb Sync Page</a>
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import API from '../../../api';
import { PARTNER_AIRBNB_SETTINGS_ENDPOINT } from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import Modal from '../../generic/modal/modal.vue';

export default {
    name: 'AirbnbSettingsModalForm',
    components: {
        BusyModal,
        FormInput,
        FormSelect,
        FormTextarea,
        Modal,
    },
    props: {
        modalId: String,
        partnerSettings: Object,
    },
    mounted() {
        this.values = this.getInitialValues();
    },
    data() {
        return {
            loading: false,
            values: null,
            rateRulesExportPeriodOptions: [
                { value: 6, text: '6 Months' },
                { value: 12, text: '1 Year' },
                { value: 18, text: '18 Months' },
            ],
            bookingLeadTimeOptions: [
                { value: 0, text: 'Till check-in time' },
                { value: 1, text: '1 hour' },
                { value: 2, text: '2 hours' },
                { value: 3, text: '3 hours' },
                { value: 4, text: '4 hours' },
                { value: 5, text: '5 hours' },
                { value: 6, text: '6 hours' },
                { value: 7, text: '7 hours' },
                { value: 8, text: '8 hours' },
                { value: 9, text: '9 hours' },
                { value: 10, text: '10 hours' },
                { value: 11, text: '11 hours' },
                { value: 12, text: '12 hours' },
                { value: 13, text: '13 hours' },
                { value: 14, text: '14 hours' },
                { value: 15, text: '15 hours' },
                { value: 16, text: '16 hours' },
                { value: 17, text: '17 hours' },
                { value: 18, text: '18 hours' },
                { value: 19, text: '19 hours' },
                { value: 20, text: '20 hours' },
                { value: 21, text: '21 hours' },
                { value: 22, text: '22 hours' },
                { value: 23, text: '23 hours' },
                { value: 24, text: '24 hours' },
                { value: 48, text: '48 hours' },
                { value: 72, text: '72 hours' },
                { value: 168, text: '168 hours' },
            ],
            allowRequestToBookOptions: [
                { value: true, text: 'Yes' },
                { value: false, text: 'No' },
            ],
            fetchInquiriesOptions: [
                { value: true, text: 'Yes' },
                { value: false, text: 'No' },
            ],
            addDamageToCleaningFeeOptions: [
                { value: true, text: 'Add to Cleaning Fee' },
                { value: false, text: 'Add to Nightly Rate' },
            ],
            addMgmtFeeToCleaningFeeOptions: [
                { value: true, text: 'Add to Cleaning Fee' },
                { value: false, text: 'Add to Nightly Rate' },
            ],
        };
    },
    methods: {
        getInitialValues() {
            return {
                commission: this.partnerSettings.commission,
                rate_rules_export_period: this.partnerSettings.rate_rules_export_period,
                booking_lead_time: this.partnerSettings.booking_lead_time,
                allow_request_to_book: this.partnerSettings.allow_request_to_book,
                fetch_inquiries: this.partnerSettings.fetch_inquiries,
                add_damage_to_cleaning_fee: this.partnerSettings.add_damage_to_cleaning_fee,
                add_mgmt_fee_to_cleaning_fee: this.partnerSettings.add_mgmt_fee_to_cleaning_fee,
                house_rules: this.partnerSettings.house_rules,
            };
        },
        setData() {
            this.$refs.form.setValues({
                rate_rules_export_period: this.values.rate_rules_export_period,
                booking_lead_time: this.values.booking_lead_time,
                commission: this.values.commission,
                house_rules: this.values.house_rules,

            })
        },
        hideModal() {
            this.$bvModal.hide(this.modalId);
        },
        hiddenModal() {
            this.$emit('hidden');
        },
        async save() {
            this.loading = true;

            try {
                await API.patch(PARTNER_AIRBNB_SETTINGS_ENDPOINT(), this.values);
                this.$emit('success-save');
                this.$nextTick(() => {
                    this.hideModal();
                });
            } catch (error) {
                const { generic, fields } = formatResponseError(error);
                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.form.setErrors(fields);
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
